import { sanitize } from 'isomorphic-dompurify'
import { useTranslation } from 'react-i18next'

import { AchievementBadge } from '@percent/workplace-giving/common/components/AchievementBadge/AchievementBadge'
import { Badge, Flex, Tooltip, Typography } from '@percent/lemonade'
import { useDateTimeFmt } from '@percent/workplace-giving/common/hooks'
import {
  AccountAchievementsResponse,
  Condition
} from '@percent/workplace-giving/api/account/achievements/achievements.types'
import classes from './BadgeCard.module.scss'

type RuleAndAchievement = AccountAchievementsResponse['rulesAndAchievements'][number]

type BadgeCardProps = Readonly<{
  achievement: RuleAndAchievement['achievement']
  accountAchievement: RuleAndAchievement['accountAchievement']
  rule: RuleAndAchievement['rule']
  count: number
}>

function TooltipContent({ name, conditions }: Readonly<{ name: string; conditions: Condition }>) {
  return (
    <Flex direction="column" py={8} px={4}>
      <Typography variant="default-medium">{name}</Typography>
      <Typography variant="small" $color="gray400">
        {conditions.description}
      </Typography>
      <ul className={classes.StepsList}>
        {conditions.conditions?.flatMap(({ factKind, operator, description, descriptionLines }) =>
          (descriptionLines ?? [description]).map(
            desc =>
              desc && (
                <li
                  key={`${operator}-${factKind}-${desc}`}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitize(desc, { ALLOWED_TAGS: ['p', 'br'] })
                  }}
                />
              )
          )
        )}
      </ul>
    </Flex>
  )
}

export function BadgeCard({ achievement, accountAchievement, rule, count }: BadgeCardProps) {
  const { t } = useTranslation()
  const { formatDateWODay } = useDateTimeFmt()

  const isUnlocked = !!accountAchievement || rule.conditions.pass

  return (
    <Tooltip
      lightBg
      lineClamp={0}
      placement="bottom-start"
      content={<TooltipContent name={achievement.name} conditions={rule.conditions} />}
    >
      <div className={classes.CardWrapper} data-testid="badge-card">
        <div className={classes.BadgeImage}>
          <AchievementBadge image={achievement.image} name={achievement.name} isBlocked={!isUnlocked} size="large" />
          {count > 1 ? (
            <span className={classes.CountBadge}>
              <Badge variant="default" shape="oval">
                x{count}
              </Badge>
            </span>
          ) : null}
        </div>
        <Flex direction="column" gap={4}>
          <Typography variant="large-bold">{achievement.name}</Typography>
          <Typography variant="small" $color="gray400">
            {isUnlocked && accountAchievement?.createdAt
              ? t('workplace_giving.gamification.completedOn', {
                  date: formatDateWODay(accountAchievement.createdAt)
                })
              : t('workplace_giving.gamification.notUnlocked')}
          </Typography>
        </Flex>
      </div>
    </Tooltip>
  )
}
